import { ApiClient } from './ApiClient';
import { InvoiceDTO } from '../domain/models';
import { mapUnitForApi } from '../utils/unitMapping';

export type PdfDocumentDTO = {
  content?: string[] | string | number[]; // Backend kann verschiedene Formen senden
  fileName?: string;
};

// Typen für den Export-Payload Richtung Backend (OpenAPI)
type ApiUnit = 'PIECE' | 'KG' | 'TO';
interface InvoiceExportProductPayload {
  id?: number;
  productKey?: string;
  name?: string;
  description?: string;
  unitPrice?: number; // bereits normalisiert als Zahl
  unit: ApiUnit;      // Pflichtfeld im Export
}
interface InvoiceExportItemPayload {
  product: InvoiceExportProductPayload;
  quantity: number; // normalisiert (>=0)
}
interface InvoiceExportCustomerPayload {
  id?: number;
  customerNumber?: string;
  firstname?: string;
  lastname?: string;
  street?: string;
  buildingNumber?: string;
  postcode?: string;
  city?: string;
  country?: string;
  telephone?: string;
  idCardNr?: string;
  mail?: string;
}
interface InvoiceExportPayload {
  customer: InvoiceExportCustomerPayload;
  invoiceNumber?: string;
  issueDate?: string; // YYYY-MM-DD
  items: InvoiceExportItemPayload[];
}

// Hilfsfunktion zur Reduktion von verschachtelten Bedingungen
function pickFields<T extends object>(src: any, fields: (keyof T)[]): Partial<T> {
  const out: Partial<T> = {};
  for (const f of fields) {
    let v = src?.[f as string];
    if (v == null) continue;
    if (typeof v === 'string') {
      v = v.trim();
      if (v === '') continue;
    }
    (out as any)[f] = v;
  }
  return out;
}

export class InvoiceApiService {
  constructor(private readonly http: ApiClient) {}

  private buildCustomerPayload(srcCustomer: any): InvoiceExportCustomerPayload {
    return pickFields<InvoiceExportCustomerPayload>(srcCustomer || {}, ['id','customerNumber','firstname','lastname','street','buildingNumber','postcode','city','country','telephone','idCardNr','mail']) as InvoiceExportCustomerPayload;
  }

  private buildProductPayload(p: any, index: number): InvoiceExportProductPayload {
    const base = pickFields<InvoiceExportProductPayload>(p || {}, ['id','productKey','name','description']);
    if (p?.unitPrice !== undefined && p?.unitPrice !== null && isFinite(Number(p.unitPrice))) {
      (base as any).unitPrice = Number(p.unitPrice);
    }
    (base as any).unit = (mapUnitForApi(p?.unit as any) || 'PIECE') as ApiUnit;
    // Fallback für fehlende Artikelnummer (productKey)
    let pk = (base as any).productKey as string | undefined;
    if (!pk || !pk.trim()) {
      if (p?.id != null) pk = `ID${p.id}`;
      else if (typeof p?.name === 'string' && p.name.trim()) {
        pk = p.name.trim().toUpperCase().replace(/[^A-Z0-9]+/g,'_').replace(/^_+|_+$/g,'').slice(0,25);
      }
      if (!pk || !pk.trim()) pk = `ITEM_${index + 1}`;
      (base as any).productKey = pk;
    }
    return base as InvoiceExportProductPayload;
  }

  private normalizeQuantity(raw: any): number {
    const n = Number(raw);
    return isFinite(n) && n > 0 ? n : 0;
  }

  private buildItemsPayload(itemsSrc: any[]): InvoiceExportItemPayload[] {
    if (!Array.isArray(itemsSrc)) return [];
    return itemsSrc
      .filter(Boolean)
      .map((it, idx) => {
        const product = this.buildProductPayload(it?.product || {}, idx);
        const quantity = this.normalizeQuantity(it?.quantity ?? it?.product?.quantity);
        return { product, quantity };
      });
  }

  // OpenAPI-konformes Payload: eingebettete Customer-/Product-Objekte gemäß InvoiceDTO.
  private buildInvoicePayload(dto: InvoiceDTO | undefined | null): InvoiceExportPayload {
    if (!dto) {
      return { customer: {}, items: [] } as InvoiceExportPayload;
    }
    const customer = this.buildCustomerPayload(dto.customer || {});
    const items = this.buildItemsPayload(dto.items as any[]);
    const payload: InvoiceExportPayload = {
      customer,
      invoiceNumber: dto.invoiceNumber?.trim() || undefined,
      issueDate: dto.issueDate?.trim() || undefined,
      items,
    };
    if (process.env.NODE_ENV !== 'production') {
      console.debug('[InvoiceApiService] export payload (spec)', payload);
    }
    return payload;
  }

  async exportInvoice(dto: InvoiceDTO): Promise<PdfDocumentDTO> {
    const payload = this.buildInvoicePayload(dto);
    return this.http.post<PdfDocumentDTO>('/invoice', payload);
  }
}

// --- PDF Hilfsfunktionen ---
function safeBase64ToBytes(base64: string): Uint8Array | null {
  if (!base64) return null;
  try {
    const binary = atob(base64);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
    return bytes;
  } catch {
    return null;
  }
}

function extractBase64(raw: string): string {
  const trimmed = raw.trim();
  // Entfernt häufige Data-URL-Präfixe
  const match = /^data:application\/pdf;?base64,(.*)$/i.exec(trimmed);
  if (match) return match[1];
  // Falls ein generisches 'base64,' vorkommt, alles dahinter nehmen
  const idx = trimmed.indexOf('base64,');
  if (idx >= 0) return trimmed.slice(idx + 'base64,'.length);
  return trimmed;
}

function pdfToBlob(doc: PdfDocumentDTO): Blob | null {
  const c: unknown = doc.content as unknown;
  if (c == null) return null;

  // Fall: number[]
  if (Array.isArray(c) && c.length > 0 && typeof c[0] === 'number') {
    try {
      const bytes = new Uint8Array(c as number[]);
      if (!bytes.length) return null;
      return new Blob([bytes], { type: 'application/pdf' });
    } catch {
      return null;
    }
  }
  // Fall: string[] (zusammengesetzte Base64-Fragmente)
  if (Array.isArray(c) && (c as unknown[]).every(x => typeof x === 'string')) {
    const base64 = (c as string[]).join('').trim();
    if (!base64) return null;
    const bytes = safeBase64ToBytes(extractBase64(base64));
    return bytes ? new Blob([bytes], { type: 'application/pdf' }) : null;
  }
  // Fall: einzelner Base64-String
  if (typeof c === 'string') {
    const base64 = extractBase64(c);
    if (!base64) return null;
    const bytes = safeBase64ToBytes(base64);
    return bytes ? new Blob([bytes], { type: 'application/pdf' }) : null;
  }
  return null;
}

export function downloadPdf(doc: PdfDocumentDTO) {
  const name = doc.fileName || 'invoice.pdf';
  const blob = pdfToBlob(doc);
  if (!blob) return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function openPdf(doc: PdfDocumentDTO) { // bewusst exportiert für zukünftige Nutzung
  const blob = pdfToBlob(doc);
  if (!blob) return;
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
}

export function printPdf(doc: PdfDocumentDTO) {
  const blob = pdfToBlob(doc);
  if (!blob) return;
  const url = URL.createObjectURL(blob);
  const win = window.open(url, '_blank');
  if (!win) return; // popup blocked
  // Try to auto-print once the PDF is loaded
  const tryPrint = () => {
    try {
      win.focus();
      // Some browsers need a delay before print
      setTimeout(() => { try { win.print(); } catch {} }, 300);
    } catch {}
  };
  // Fallback: attempt after some delay
  win.addEventListener('load', tryPrint);
  setTimeout(tryPrint, 1000);
  // Revoke URL später (nicht zu früh, damit Druck nicht abbricht)
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
